import Image from 'next/image';

import Text from '@/components/common/Text/Text';
import Close from 'public/img/icon/close.svg';

/**
 * Closet-description copy for each tier's info tooltip, keyed by slug. The
 * title and price are passed in so they stay consistent with the radio label
 * (which reads its price from live product data) rather than drifting from a
 * second hardcoded number.
 */
type TierCopy = {
  summary: string;
  accessNote: string;
  includes: string[];
};

const TIER_COPY: Record<string, TierCopy> = {
  premier: {
    summary: '1 item at a time from the Premier closet.',
    accessNote: 'Includes access to the following items:',
    includes: ['Jewelry up to $1000', 'Select bags']
  },
  classique: {
    summary: '1 item at a time from the Classique closet.',
    accessNote:
      'Includes access to the following items with retail value up to $4,000:',
    includes: ['Jewelry', 'Bags']
  },
  couture: {
    summary: '1 item at a time from the Couture closet.',
    accessNote: 'Includes full access to the following items:',
    includes: ['Jewelry', 'Bags', 'Limited Edition Pieces']
  },
  'classique-plus': {
    summary: '2 items at a time from the Classique closet.',
    accessNote:
      'Includes access to the following items with retail value up to $4,000:',
    includes: ['Jewelry', 'Bags']
  },
  'couture-plus': {
    summary:
      '2 items at a time. 1 from the Classique closet + 1 from the Couture closet.',
    accessNote: 'Includes full access to the following items:',
    includes: ['Jewelry', 'Bags', 'Limited Edition Pieces']
  }
};

type TierTooltipContentProps = {
  slug: string;
  /** Display name, e.g. "Classique" — rendered as "CLASSIQUE TIER". */
  name: string;
  /** Whole-dollar price, e.g. "$119" — rendered as "$119/MONTH". */
  price: string;
};

export function TierTooltipContent(props: TierTooltipContentProps) {
  const { slug, name, price } = props;
  const copy = TIER_COPY[slug] ?? TIER_COPY.classique;

  return (
    <div className='TierTooltip'>
      <div className='TierTooltip-header'>
        <Text
          variant='title-xxs-700-upper'
          className='TierTooltip-title'
        >
          {name} Tier
          <br />
          {price}/Month
        </Text>
        {/*
          The tooltip opens and closes on hover; this X is cosmetic (and the
          affordance for touch devices, where there is no hover), so it is
          decorative rather than an interactive close control.
        */}
        <span
          className='TierTooltip-close'
          aria-hidden='true'
        >
          <Image
            src={Close}
            alt=''
            width={16}
            height={16}
          />
        </span>
      </div>
      <Text
        variant='text-s'
        className='TierTooltip-desc'
      >
        {copy.summary}
      </Text>
      <Text
        variant='text-s'
        className='TierTooltip-desc'
      >
        {copy.accessNote}
      </Text>
      <ul className='TierTooltip-list'>
        {copy.includes.map((item) => (
          <li key={item}>
            <Text variant='text-s'>{item}</Text>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default TierTooltipContent;
